import i18n from '@/i18n';
import * as sentry from '@sentry/vue';
import axios, { AxiosError, AxiosResponse } from 'axios';

interface ErrorData {
   errors?: Record<string, any>;
   errorMessage?: string;
   message?: string;
}

let errorQueue: string[] = [];
let errorTimeout: NodeJS.Timeout | null = null;

function getErrorMessage(error: AxiosError): string | null {
   try {
      const errorData = error?.response?.data as ErrorData;

      if (errorData?.errors) {
         return Object.values(errorData.errors).join('\r\n');
      }
      if (errorData?.errorMessage) return errorData.errorMessage;
      if (errorData?.message) return errorData.message;

      if ([400, 422].includes(error.status as number)) {
         return i18n.global.t('errors.validation');
      }

      if (error.status === 403) {
         return i18n.global.t('errors.account-not-allowed');
      }

      if (error.status === 404) {
         return i18n.global.t('errors.account-not-found');
      }

      return null;
   } catch {
      return null;
   }
}

function captureSentryError(error: AxiosError): void {
   try {
      const errorName = `[${error.status} ${error.code}] - ${error.config?.url}`;
      sentry.captureException(new Error(errorName), {
         extra: {
            stack: error.stack,
            request: {
               url: error.config?.url,
               method: error.config?.method,
               headers: error.config?.headers,
               params: error.config?.params,
            },
            response: {
               code: error.code,
               message: error.message,
               status: error.status,
               data: error.response?.data,
            },
         },
      });
   } catch {
      console.warn('Error while capturing error');
   }
}

function notifyErrors() {
   if (errorQueue.length > 0) {
      const errorMessage = errorQueue.join('\r\n');
      notifyError({ title: errorMessage || i18n.global.t('generic.feedback.error') });
      errorQueue = [];
   }
}

const errorResponseSuccessInterceptor = function (response: AxiosResponse<any>) {
   return response;
};

const errorResponseErrorInterceptor = function (error: AxiosError<ErrorData>) {
   if ([400, 404, 500].includes(error.status as number)) captureSentryError(error);
   const isCancelException = axios.isCancel(error);
   const isAbortedException = error.message === 'Request aborted';

   if (isCancelException || isAbortedException) return Promise.reject(error);

   let errorMessage = getErrorMessage(error);

   if (!errorMessage) {
      errorMessage = `${i18n.global.t('generic.feedback.error')}\n${(error as any).message ?? ''}`;
   }

   if (!errorQueue.includes(errorMessage)) {
      errorQueue.push(errorMessage);
   }

   if (!errorTimeout) {
      errorTimeout = setTimeout(() => {
         notifyErrors();
         errorTimeout = null;
      }, 300);
   }

   return Promise.reject(error);
};

export { errorResponseErrorInterceptor, errorResponseSuccessInterceptor };
